import { AnyAri } from '@atlassian/ari';
import type { RequestContext } from '@post-office/shared-contracts/shared-request-middleware-context/requestContext';

const normalizeEntitlementId = (value: string): string =>
	AnyAri.check(value) ? AnyAri.parse(value).resourceId : value;

/**
 * Fetches the Jira onboarding URL from GUPS for a given org.
 * @param requestContext - The request context containing GUPS client and orchestration context
 * @param orgId - The organization ID to fetch the onboarding URL for
 * @param entitlementId - Entitlement ID (raw UUID or full ARI) to filter the TWC onboarding context
 * @param tenantId - Tenant (cloudId) to include in the filter; takes precedence over orchestrationContext.tenantId
 * @param messageInstanceId - Optional message instance ID for logging
 * @param messageTemplateId - Optional message template ID for logging
 * @returns The onboarding URL string, or undefined if not found
 */
export const getJiraOnboardingUrl = async (
	requestContext: RequestContext,
	orgId: string | undefined,
	entitlementId: string | undefined,
	tenantId?: string,
	messageInstanceId?: string,
	messageTemplateId?: string,
): Promise<string | undefined> => {
	const { logger, graphqlClients } = requestContext;

	if (!orgId) {
		logger.error({ messageInstanceId, messageTemplateId }, 'Org ID not available');
		return undefined;
	}

	if (!entitlementId) {
		logger.error({ messageInstanceId, messageTemplateId }, 'Entitlement ID not available');
		return undefined;
	}

	const normalizedEntitlementId = normalizeEntitlementId(entitlementId);
	const resolvedTenantId = tenantId ?? requestContext.orchestrationContext.tenantId;

	const filter = {
		entitlementId: normalizedEntitlementId,
		...(resolvedTenantId ? { tenantId: resolvedTenantId } : {}),
	};

	try {
		const orgProfile = await graphqlClients.growthUnifiedProfile.getOrgProfile(orgId, filter);
		const [onboardingContext] = orgProfile.twcOnboardingContext ?? [];

		if (!onboardingContext) {
			logger.error(
				{ messageInstanceId, messageTemplateId, orgId, entitlementId },
				'No TWC onboarding context found in GUPS profile',
			);
			return undefined;
		}

		const { onboardingUrl } = onboardingContext;

		if (!onboardingUrl) {
			logger.error(
				{ messageInstanceId, messageTemplateId, orgId, entitlementId },
				'No onboarding URL found in TWC onboarding context',
			);
			return undefined;
		}

		logger.info(
			{ messageInstanceId, messageTemplateId, orgId, entitlementId, onboardingUrl },
			'Successfully fetched onboarding URL from GUPS',
		);
		return onboardingUrl;
	} catch (error) {
		logger.error(
			{ messageInstanceId, messageTemplateId, orgId, entitlementId, error },
			'Error fetching onboarding URL from GUPS',
		);
		return undefined;
	}
};
